import { cn } from '@/lib/utils';
import type { LeaderboardCategory, LeaderboardEntry } from '@/types/leaderboard';
import { imageUrl } from '@/utils/image-url';
import Ionicons from '@expo/vector-icons/Ionicons';
import { router } from 'expo-router';
import { Image, Pressable, Text, View } from 'react-native';

interface LeaderboardRowProps {
  entry: LeaderboardEntry;
  category: LeaderboardCategory;
  isCurrentUser?: boolean;
}

function getRankStyle(rank: number): string {
  if (rank === 1) return 'text-yellow-500';
  if (rank === 2) return 'text-gray-400';
  if (rank === 3) return 'text-amber-600';
  return 'text-foreground/30';
}

function getCategoryLabel(category: LeaderboardCategory): string {
  return category === 'check_ins' ? 'check-ins' : 'reviews';
}

export function LeaderboardRow({ entry, category, isCurrentUser }: LeaderboardRowProps) {
  const avatarSrc = imageUrl(entry.avatarUrl, 'thumbnail');
  const displayName = entry.displayName || entry.username || 'Unknown';

  const handlePress = () => {
    if (entry.username) {
      router.push(`/(root)/profile/${entry.username}`);
    }
  };

  return (
    <Pressable
      onPress={handlePress}
      className={cn(
        'flex-row items-center px-4 py-3 border-b border-border active:bg-surface',
        isCurrentUser && 'bg-muted',
      )}
    >
      <Text
        className={cn(
          'text-lg font-bold',
          getRankStyle(entry.rank),
        )}
        style={{ width: 36, textAlign: 'center' }}
      >
        {entry.rank}
      </Text>

      {avatarSrc ? (
        <Image
          source={{ uri: avatarSrc }}
          style={{ width: 40, height: 40, borderRadius: 20 }}
          accessibilityLabel={`${displayName}'s avatar`}
        />
      ) : (
        <View
          className="bg-surface items-center justify-center"
          style={{ width: 40, height: 40, borderRadius: 20 }}
        >
          <Ionicons name="person" size={20} className="text-muted-foreground" />
        </View>
      )}

      <View className="flex-1" style={{ marginLeft: 12 }}>
        <Text className="text-foreground font-semibold" numberOfLines={1}>
          {displayName}
        </Text>
        {entry.username && (
          <Text className="text-xs text-muted-foreground" numberOfLines={1}>
            @{entry.username}
          </Text>
        )}
      </View>

      <Text className="text-foreground font-semibold">
        {entry.count}
        <Text className="text-xs text-muted-foreground font-normal">
          {' '}{getCategoryLabel(category)}
        </Text>
      </Text>
    </Pressable>
  );
}
